<!-- src/components/Stepper/CompanyData.vue -->
<template>
  <div class="company-data-step">
    <!-- Chatbot Info Modal (erscheint nur beim ersten Aufruf) -->
    <div v-if="showChatbotModal" class="chatbot-modal-overlay">
      <div class="chatbot-modal">
        <div class="chatbot-modal-header">
          <i class="bi bi-info-circle-fill"></i>
          <h4>Important User Information</h4>
        </div>
        <div class="chatbot-modal-body">
          <div class="info-section">
            <div class="info-block">
              <div class="info-block-header">
                <i class="bi bi-chat-dots-fill"></i>
                <h5>Chatbot Assistance Available</h5>
              </div>
              <p>
                Our intelligent chatbot is always available in the bottom right corner to assist you. 
                The chatbot has access to all information you've entered in previous steps and can provide 
                contextual help or answer any questions you might have about the process.
              </p>
            </div>
            
            <div class="info-block">
              <div class="info-block-header">
                <i class="bi bi-pencil-square"></i>
                <h5>Optional Fields</h5>
              </div>
              <p>
                All fields in this form are optional. However, providing as much information 
                as possible will help us find the most suitable solutions for your food surplus. The more details you provide, 
                the better recommendations you'll receive.
              </p>
            </div>
          </div>
        </div>
        <div class="chatbot-modal-footer">
          <button @click="acknowledgeChatbotInfo" class="btn-confirm">OK, Got it!</button>
        </div>
      </div>
    </div>

    <StepperHeading :title="'Enter Company Data'" :step="1">
      <template #help>
        <p>
          In this step, you enter basic information about your company, including:
          <strong>Company Name</strong>,
          <strong>Business Category</strong>,
          and one or more <strong>NACE Codes</strong>.
        </p>
        <p>
          <strong>Business Category:</strong> Describes your general industry, e.g.
          <em>bakery, dairy production</em>, or <em>fruit and vegetable trade</em>.
        </p>
        <p>
          <strong>NACE Codes:</strong> A standardized EU system for categorizing businesses.
          You can <em>filter</em> codes by level (Section, Division, Group, Class) or search by code/description.
          <strong>Tip:</strong> You can ask the chatbot for appropriate NACE codes for your industry!
        </p>
      </template>
    </StepperHeading>

    <!-- Company Data Form -->
    <form class="company-form">
      <div class="form-section">
        <h3 class="section-title">Company Information</h3>
        
        <!-- Company Name -->
        <div class="form-field">
          <label for="companyName">Company Name</label>
          <div class="input-wrapper">
            <i class="bi bi-building input-icon"></i>
            <input
              type="text"
              id="companyName"
              v-model="companyProfile.name"
              placeholder="Enter company name"
            />
          </div>
        </div>

        <!-- Business Category -->
        <div class="form-field">
          <label for="businessCategory">Business Category</label>
          <div class="input-wrapper">
            <i class="bi bi-briefcase input-icon"></i>
            <input
              type="text"
              id="businessCategory"
              v-model="companyProfile.businessCategory"
              placeholder="Enter business category"
            />
          </div>
        </div>

        <!-- NACE Codes -->
        <div class="form-field">
          <label for="naceCodeSearch">NACE Code(s)</label>
          <div class="nace-help-hint">
            <i class="bi bi-chat-text"></i> Not sure which NACE codes to use? Ask our chatbot for recommendations based on your business!
          </div>
          
          <div class="field-action-row">
            <label class="filter-label">Filter by Level:</label>
            <select
              class="level-filter"
              v-model="naceFilterLevel"
              @change="filterNaceCodes"
            >
              <option value="all">All</option>
              <option value="Section">Section</option>
              <option value="Division">Division</option>
              <option value="Group">Group</option>
              <option value="Class">Class</option>
            </select>
          </div>

          <div class="input-wrapper search-wrapper position-relative">
            <i class="bi bi-search input-icon"></i>
            <input
              type="text"
              id="naceCodeSearch"
              class="search-input"
              placeholder="Search or add NACE Codes..."
              v-model="naceCodeSearch"
              @focus="isNaceDropdownOpen = true"
              @input="filterNaceCodes"
              autocomplete="off"
            />
            
            <div
              class="dropdown-menu"
              :class="{ 'show': isNaceDropdownOpen }"
              v-if="isNaceDropdownOpen"
            >
              <div class="dropdown-items">
                <button
                  class="dropdown-item"
                  v-for="nace in filteredNaceCodes"
                  :key="nace._id"
                  @click.prevent="addNaceCode(nace)"
                >
                  {{ nace.code }} - {{ nace.description }}
                  <span v-if="nace.level" class="item-badge">{{ nace.level }}</span>
                </button>
                <div class="dropdown-item empty" v-if="filteredNaceCodes.length === 0">
                  No matches found
                </div>
              </div>
            </div>
          </div>

          <div class="selected-items">
            <div
              v-for="(naceObj, idx) in companyProfile.naceCodes"
              :key="naceObj.code"
              class="selected-item"
            >
              <span class="item-code">{{ naceObj.code }}</span>
              <span class="item-description">{{ naceObj.description }}</span>
              <button 
                type="button" 
                class="remove-btn" 
                @click="removeNaceCode(naceObj)"
                aria-label="Remove NACE code"
              >
                <i class="bi bi-x"></i>
              </button>
            </div>
            <div class="empty-selection" v-if="companyProfile.naceCodes.length === 0">
              No NACE codes selected
            </div>
          </div>
        </div>

        <!-- Company Description -->
        <div class="form-field">
          <label for="description">Company Description</label>
          <div class="input-wrapper textarea-wrapper">
            <i class="bi bi-file-text input-icon"></i>
            <textarea
              id="description"
              v-model="companyProfile.description"
              placeholder="Enter a short text to better describe your company's business field"
              rows="4"
            ></textarea>
          </div>
        </div>
      </div>

      <!-- Location Section -->
      <div class="form-section">
        <h3 class="section-title">Location Information</h3>
        
        <div class="form-field">
          <label for="address">Street Address</label>
          <div class="input-wrapper">
            <i class="bi bi-geo-alt input-icon"></i>
            <input
              type="text"
              id="address"
              v-model="companyProfile.location.address"
              placeholder="Enter street address"
            />
          </div>
        </div>
        
        <div class="form-row">
          <div class="form-field">
            <label for="city">City</label>
            <div class="input-wrapper">
              <i class="bi bi-buildings input-icon"></i>
              <input
                type="text"
                id="city"
                v-model="companyProfile.location.city"
                placeholder="Enter city"
              />
            </div>
          </div>
          
          <div class="form-field">
            <label for="postalCode">Postal Code</label>
            <div class="input-wrapper">
              <i class="bi bi-mailbox input-icon"></i>
              <input
                type="text"
                id="postalCode"
                v-model="companyProfile.location.postalCode"
                placeholder="Enter postal code"
              />
            </div>
          </div>
        </div>
        
        <div class="form-field">
          <label for="country">Country</label>
          <div class="input-wrapper">
            <i class="bi bi-globe input-icon"></i>
            <input
              type="text"
              id="country"
              v-model="companyProfile.location.country"
              placeholder="Enter country"
            />
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import axios from 'axios';
import { ref, computed } from 'vue';
import { useStepperStore } from '@/stores/stepper';
import { usePageContextStore } from '@/stores/pageContext';
import StepperHeading from './StepperHeading.vue';

export default {
  name: 'CompanyData',
  components: { StepperHeading },
  setup() {
    const stepperStore = useStepperStore();
    const pageContextStore = usePageContextStore();
    const companyProfile = stepperStore.companyProfile;

    return {
      companyProfile,
      stepperStore,
      pageContextStore,
    };
  },
  data() {
    return {
      naceCodes: [],
      filteredNaceCodes: [],
      isNaceDropdownOpen: false,
      naceCodeSearch: '',
      naceFilterLevel: 'all',
      showChatbotModal: false
    };
  },
  async mounted() {
    try {
      const resp = await axios.get('/nace_codes');
      this.naceCodes = resp.data;
      this.filteredNaceCodes = this.naceCodes;
    } catch (error) {
      console.error('Error loading NACE codes:', error);
      this.naceCodes = [];
      this.filteredNaceCodes = [];
    }

    this.pageContextStore.setPageContext(
      'Enter Company Data',
      'Here you can input your company details including NACE codes. The address you enter is geocoded once when you proceed.',
      'A drop-down with NACE codes is available for classification.'
    );

    document.addEventListener('click', this.handleClickOutside);
    
    // Prüfen, ob Chatbot-Info bereits angezeigt wurde
    const chatbotInfoShown = sessionStorage.getItem('chatbotInfoShown');
    if (!chatbotInfoShown) {
      this.showChatbotModal = true;
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    acknowledgeChatbotInfo() {
      this.showChatbotModal = false;
      sessionStorage.setItem('chatbotInfoShown', 'true');
    },
    filterNaceCodes() {
      let arr = this.naceCodes;
      if (this.naceFilterLevel !== 'all') {
        arr = arr.filter(item => item.level === this.naceFilterLevel);
      }
      const query = this.naceCodeSearch.trim().toLowerCase();
      if (query) {
        arr = arr.filter(item =>
          item.code.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
        );
      }
      const chosenCodes = this.companyProfile.naceCodes.map(obj => obj.code);
      arr = arr.filter(item => !chosenCodes.includes(item.code));
      this.filteredNaceCodes = arr;
      this.isNaceDropdownOpen = true;
    },
    addNaceCode(nace) {
      const newObj = { code: nace.code, description: nace.description };
      const existingCodes = this.companyProfile.naceCodes.map(o => o.code);
      if (!existingCodes.includes(nace.code)) {
        this.companyProfile.naceCodes.push(newObj);
      }
      this.naceCodeSearch = '';
      this.isNaceDropdownOpen = false;
    },
    removeNaceCode(naceObj) {
      this.companyProfile.naceCodes = this.companyProfile.naceCodes.filter(
        c => c.code !== naceObj.code
      );
    },
    handleClickOutside(e) {
      if (!this.$el.contains(e.target)) {
        this.isNaceDropdownOpen = false;
      }
    },
  },
};
</script>

<style scoped>
.company-data-step {
  width: 100%;
}

.company-form {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

/* Neue Card-Designs */
.form-section {
  background-color: #ffffff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05), 0 5px 10px rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(135, 177, 64, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.form-section:hover {
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.07), 0 6px 12px rgba(0, 0, 0, 0.03);
  transform: translateY(-2px);
}

.form-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #87B140, #9AC95A);
}

.section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 24px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, #87B140, #9AC95A);
  border-radius: 3px;
}

/* Beibehaltene ursprüngliche Stile für innere Elemente */
.form-field {
  margin-bottom: 20px;
}

.form-field:last-child {
  margin-bottom: 0;
}

.form-row {
  display: flex;
  gap: 20px;
}

.form-row .form-field {
  flex: 1;
}

label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #555;
  font-size: 0.95rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.input-icon {
  position: absolute;
  left: 10px;
  color: #87b140;
  font-size: 1.1rem;
  pointer-events: none;
}

input, textarea, select {
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  transition: all 0.3s ease;
}

input:focus, textarea:focus, select:focus {
  outline: none;
  border-color: #87b140;
  box-shadow: 0 0 0 2px rgba(135, 177, 64, 0.2);
}

textarea {
  resize: vertical;
  min-height: 100px;
}

.field-action-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.filter-label {
  margin-bottom: 0;
  margin-right: 10px;
  font-size: 0.9rem;
}

.level-filter {
  width: auto;
  padding: 6px 12px 6px 12px;
  border-radius: 4px;
  font-size: 0.9rem;
}

/* Field hint styling */
.field-hint {
  margin-top: 6px;
  font-size: 0.85rem;
  color: #666;
  display: flex;
  align-items: center;
  gap: 6px;
}

.field-hint i {
  color: #87B140;
}

/* Dropdown styling */
.dropdown-menu {
  background: white;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 5px;
  max-height: 250px;
  overflow-y: auto;
  z-index: 9999;
  border: 1px solid #eee;
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
}

.dropdown-menu.show {
  display: block;
}

.dropdown-items {
  padding: 5px 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  text-align: left;
  padding: 10px 15px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.95rem;
  color: #333;
  transition: background-color 0.2s;
}

.dropdown-item:hover {
  background-color: rgba(135, 177, 64, 0.1);
}

.dropdown-item.empty {
  color: #888;
  font-style: italic;
}

.item-badge {
  font-size: 0.8rem;
  background: #f0f0f0;
  padding: 2px 8px;
  border-radius: 12px;
  color: #666;
}

/* Selected items styling */
.selected-items {
  margin-top: 15px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.selected-item {
  display: flex;
  align-items: center;
  background: rgba(135, 177, 64, 0.1);
  border: 1px solid rgba(135, 177, 64, 0.3);
  border-radius: 20px;
  padding: 6px 10px;
  font-size: 0.9rem;
}

.item-code {
  font-weight: 600;
  margin-right: 5px;
}

.item-description {
  color: #555;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.remove-btn {
  background: none;
  border: none;
  color: #666;
  margin-left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  padding: 0;
  font-size: 1rem;
  transition: all 0.2s;
}

.remove-btn:hover {
  background-color: rgba(0, 0, 0, 0.1);
  color: #e74c3c;
}

.empty-selection {
  color: #888;
  font-style: italic;
  padding: 5px 0;
}

/* NACE Help Hint */
.nace-help-hint {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 12px;
  font-size: 0.9rem;
  color: #555;
  display: flex;
  align-items: center;
  gap: 8px;
  border-left: 2px solid #87B140;
}

.nace-help-hint i {
  color: #87B140;
  font-size: 1rem;
}

/* Chatbot Modal Styling */
.chatbot-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.chatbot-modal {
  background-color: white;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.chatbot-modal-header {
  background-color: #87B140; /* Einzelner Grünton statt Gradient */
  color: white;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.chatbot-modal-header i {
  font-size: 1.5rem;
}

.chatbot-modal-header h4 {
  margin: 0;
  font-size: 1.4rem;
  font-weight: 600;
}

.chatbot-modal-body {
  padding: 20px;
  color: #444;
  font-size: 1.05rem;
  line-height: 1.6;
}

.info-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-block {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e9ecef;
}

.info-block-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.info-block-header i {
  font-size: 1.3rem;
  color: #87B140;
}

.info-block-header h5 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

.info-block p {
  margin: 0;
  color: #555;
  font-size: 0.95rem;
}

.chatbot-modal-footer {
  padding: 12px 20px 20px;
  display: flex;
  justify-content: flex-end;
}

.btn-confirm {
  background-color: #87B140; /* Einzelner Grünton statt Gradient */
  color: white;
  border: none;
  border-radius: 8px; /* Weniger abgerundete Ecken für ein dezenteres Design */
  padding: 10px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.btn-confirm:hover {
  background-color: #75a236; /* Dunklerer Ton beim Hover */
  transform: translateY(-1px);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
}

@media (max-width: 768px) {
  .form-section {
    padding: 20px;
  }
  
  .form-row {
    flex-direction: column;
    gap: 15px;
  }
  
  input, textarea, select {
    padding: 10px 10px 10px 35px;
    font-size: 0.95rem;
  }
  
  .input-icon {
    font-size: 1rem;
  }
  
  .section-title {
    font-size: 1.15rem;
    margin-bottom: 18px;
  }
  
  .chatbot-modal {
    width: 95%;
  }
  
  .chatbot-modal-header h4 {
    font-size: 1.2rem;
  }
  
  .chatbot-modal-body {
    font-size: 0.95rem;
    padding: 16px;
  }
  
  .info-block {
    padding: 12px;
  }
  
  .info-block-header h5 {
    font-size: 1rem;
  }
  
  .info-block p {
    font-size: 0.9rem;
  }
}

@media (max-width: 576px) {
  .form-section {
    padding: 16px;
    border-radius: 10px;
  }
  
  .section-title {
    font-size: 1.05rem;
    margin-bottom: 16px;
  }
  
  label {
    font-size: 0.9rem;
  }
  
  .selected-item {
    font-size: 0.85rem;
    padding: 4px 8px;
  }
  
  .item-description {
    max-width: 150px;
  }
  
  .chatbot-modal-header {
    padding: 12px 16px;
  }
  
  .chatbot-modal-body {
    padding: 16px;
    font-size: 0.9rem;
  }
  
  .info-section {
    gap: 12px;
  }
  
  .info-block {
    padding: 10px;
  }
  
  .info-block-header {
    gap: 8px;
    margin-bottom: 8px;
  }
  
  .info-block-header i {
    font-size: 1.1rem;
  }
  
  .info-block-header h5 {
    font-size: 0.95rem;
  }
  
  .info-block p {
    font-size: 0.85rem;
  }
  
  .chatbot-modal-footer {
    padding: 8px 16px 16px;
  }
  
  .btn-confirm {
    font-size: 0.9rem;
    padding: 8px 20px;
  }
}
</style>